  <div class="contentInner">
    <h1>Show Meeting Titles on Room Displays using Microsoft 365 powershell</h1>
    <p class="intro">If you are a Microsoft 365 customer, and you are seeing the meeting organizer's name instead of the meeting title in
      Dash, this is because Microsoft 365 meeting rooms rename your meetings by default.  Follow these
      instructions to change your default Microsoft 365 settings, and allow Dash to show your meeting titles.</p>

    <p class="intro">Working with PowerShell can be difficult, so if you are having any problems following the
      instructions on this page, please <a routerLink="/contact">contact us</a> for help.</p>

    <h2>Part 1 - Connect to Microsoft 365 via PowerShell</h2>
    <p>There are two ways to connect to Microsoft 365 via PowerShell:</p>

    <h3>Option 1: Azure Cloud Shell</h3>
    <p>If you have an active Azure subscription, you can connect to Azure Cloud Shell by visiting
      <a target="_blank" href="https://shell.azure.com/">https://shell.azure.com/</a>.  If asked to choose between <i>Bash</i> or
      <i>PowerShell</i>, choose <i>PowerShell</i>.  Once you are connected, you can skip to part 2 of these instructions
      (changing your calendar defaults).
    </p>

    <h3>Option 2: Local PowerShell</h3>
    <p>If you do not have an Azure subscription, it is still possible to connect to your Microsoft 365 tenant by using
      PowerShell on a local Windows machine.  You will need to perform the following steps to make sure that the
      required modules are installed and set up.
    </p>
    <p><b><i>Note: We recommend that you follow our guide, but you can also follow
      <a target="_blank" href="https://docs.microsoft.com/en-us/microsoft-365/enterprise/connect-to-microsoft-365-powershell#connect-with-the-azure-cloud-shell">Microsoft's
        instructions</a> if you prefer.
    </i></b></p>
    <ol>
      <li>
        <p>The first step is to install the Exchange Online Management PowerShell module.  You can do this by running the following command:</p>
        <div class="code">Install-Module ExchangeOnlineManagement</div>
        <p><i>By default, the PowerShell Gallery (PSGallery) isn't configured as a trusted repository for Install-Module.
          If you see a message asking you if you would like to install modules from PSGallery, select Yes.</i></p>
      </li>
      <li>
        <p>Once the management module is installed, you need to connect to your Microsoft 365 tenant.  To do this, run:</p>
        <div class="code">Connect-ExchangeOnline</div>
        <p>You should see a sign-in window pop up.  Sign in as an administrator in your tenant.  If the script finishes,
        and you don't see any errors then the connection has succeeded.</p>
      </li>
    </ol>


    <h2>Part 2 - Change your calendar defaults</h2>
    <p><b><i>Note: We recommend that you follow our guide, but you can also follow
      <a target="_blank" href="https://docs.microsoft.com/en-us/exchange/troubleshoot/client-connectivity/calendar-shows-organizer-name">Microsoft's
        instructions</a> if you prefer.
    </i></b></p>
    <p>&nbsp;</p>
    <p>In this part we are going to change four settings on your meeting room mailbox.  These four settings are:</p>
    <ul>
      <li><span class="code">DeleteSubject</span> - Removes the title from meetings if <span class="code">True</span></li>
      <li><span class="code">AddOrganizerToSubject</span> - Adds the email address of the meeting's organizer to the meeting's title if <span class="code">True</span></li>
      <li><span class="code">DeleteComments</span> - Removes the meeting's description if <span class="code">True</span> (note that the meeting's description is required by Dash's check-in function)</li>
      <li><span class="code">RemovePrivateProperty</span> - Ignores the meeting's privacy/visibility if <span class="code">True</span>.  Dash will hide the title of private meetings if this setting is set to <span class="code">False</span>.</li>
    </ul>
    <p>The instructions below assume that you want to turn off all four of these settings, but if you would like to keep some of them you can modify the command in step 3 to remove the settings you do not want to change.</p>
    <p>&nbsp;</p>
    <ol>
      <li>
        <p>The first step is to decide which mailboxes you would like to change.  The following PowerShell command lists
          the name and ID of all room and equipment mailboxes in your organization:</p>
        <div class="code">Get-Mailbox -RecipientTypeDetails RoomMailbox,EquipmentMailbox | FL -Property DisplayName, UserPrincipalName</div>
      </li>
      <li>
        <p>For each mailbox that you would like to change, check its existing settings by running the following command
          (making sure to fill in the ID of your own meeting room):</p>
        <div class="code">Get-CalendarProcessing -Identity <span class="variable">THE ID OF THE ROOM YOU WOULD LIKE TO CHECK</span> | FL -Property Identity,DeleteSubject,AddOrganizerToSubject,DeleteComments,RemovePrivateProperty</div>

        <p>You should see output that looks like the following:</p>
        <pre>
Identity              : Conference Room 1
DeleteSubject         : True
AddOrganizerToSubject : True
DeleteComments        : True
RemovePrivateProperty : True
        </pre>
        <p>If your output shows all settings as <span class="code">False</span> instead of <span class="code">True</span> then your room is already configured correctly - you can skip the next step.</p>
      </li>
      <li>
        <p>The final step is to change the defaults for your room.  To do this, run the following command:</p>
        <div class="code">Set-CalendarProcessing -Identity <span class="variable">THE ID OF THE ROOM YOU WOULD LIKE TO CHANGE</span> -DeleteSubject $False -AddOrganizerToSubject $False -DeleteComments $False -RemovePrivateProperty $False</div>
        <p>That's it!  Check that the command has worked by running the <span class="code">Get-CalendarProcessing</span>
          command from step 2 again - the values should now all show <span class="code">False</span>.  Your meeting room should now
          show the correct tile for all new meetings (existing meetings will still show the organizer name).</p>
      </li>
    </ol>
    <p>You will need to repeat steps 2 and 3 above for each meeting room that you would like to change.  If you would
      like to make this change for all your room and equipment mailboxes, you can run the following command instead,
      which automatically changes the defaults for each room or equipment mailbox in your organization:</p>
    <div class="code">
      Get-Mailbox -RecipientTypeDetails RoomMailbox,EquipmentMailbox | ForEach {{ '{' }}<br>
      &nbsp;&nbsp;echo "Setting defaults for $_"<br>
      &nbsp;&nbsp;Set-CalendarProcessing -Identity $_.UserPrincipalName -DeleteSubject $False -AddOrganizerToSubject $False -DeleteComments $False -RemovePrivateProperty $False<br>
      {{ '}' }}
    </div>
  </div>
